// useMe — hydrates the signed-in user.
//
// Feeds the user-chip in the sidebar and the "logged in as X" copy on Settings.
// go-pim is company-scoped by PATH (not by a sticky tenant selection), so —
// unlike strata — this hook does NOT reconcile a current-tenant id from
// memberships; it only mirrors /v1/me into the auth store.
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { getMe } from "@/api/auth";
import { useAuth, type Me } from "@/store/auth";

export function useMe() {
  const status = useAuth((s) => s.status);
  const setUser = useAuth((s) => s.setUser);

  const q = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: status === "authenticated",
    staleTime: 5 * 60_000,
    // Background refresh so role downgrades propagate without a manual reload.
    // The response interceptor in api/client.ts also force-invalidates ['me']
    // on any 403, which closes the read-side window the moment the server
    // first rejects a stale-role action.
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
  });

  // Mirror query result into the auth store so non-query code (sidebar,
  // StrataTweaks, etc.) can read user without subscribing to react-query.
  useEffect(() => {
    if (!q.data) return;
    setUser(q.data);
  }, [q.data, setUser]);

  return q;
}

export type UseMeResult = ReturnType<typeof useMe>;
export type { Me };
